import { config } from '../config';

export interface SavedQuery {
  id: string;
  userId: string;
  name: string;
  description?: string;
  query: string;
  originalQuestion?: string;
  datacenter?: string;
  createdAt: string;
  updatedAt: string;
  lastExecutedAt?: string;
}

export interface CreateSavedQueryDto {
  query: string;
  datacenter?: string;
  originalQuestion?: string;
  name?: string;
  description?: string;
}

export interface UpdateSavedQueryDto {
  name?: string;
  description?: string;
  query?: string;
}

export interface ExecuteQueryResponse {
  success: boolean;
  results?: unknown[];
  rowCount?: number;
  executionTimeMs?: number;
  sql: string;
  error?: string;
  datacenter: string;
}

/**
 * Service for managing saved queries
 * Communicates with the Admin API saved queries endpoints
 */
export class SavedQueriesService {
  private readonly baseUrl = `${config.ADMIN_API_URL}/api/admin/v2/saved-queries`;

  /**
   * Get authorization headers for API requests
   */
  private getHeaders(token: string): HeadersInit {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Create a new saved query
   */
  async create(token: string, dto: CreateSavedQueryDto): Promise<SavedQuery> {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: this.getHeaders(token),
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(`Failed to create saved query: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get saved queries
   * @param token - Authentication token
   * @param scope - 'user' (default) or 'all' to get all queries from all users
   */
  async getAll(token: string, scope: 'user' | 'all' = 'user'): Promise<SavedQuery[]> {
    const url = scope === 'all' ? `${this.baseUrl}?scope=all` : this.baseUrl;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(token),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch saved queries: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get a specific saved query by ID
   */
  async getById(token: string, id: string): Promise<SavedQuery> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'GET',
      headers: this.getHeaders(token),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch saved query: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update a saved query
   */
  async update(token: string, id: string, dto: UpdateSavedQueryDto): Promise<SavedQuery> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PATCH',
      headers: this.getHeaders(token),
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(`Failed to update saved query: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Execute a saved query directly (without LLM)
   * Runs the SQL and returns results
   * @param token
   * @param id
   * @param datacenter - The datacenter to execute on (sent as x-c2fo-dc header, defaults to 'aws-us-west-2' on backend)
   */
  async execute(token: string, id: string, datacenter?: string): Promise<ExecuteQueryResponse> {
    const headers = {
      ...this.getHeaders(token),
      ...(datacenter && { 'x-c2fo-dc': datacenter }),
    };

    const response = await fetch(`${this.baseUrl}/${id}/execute`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to execute query: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Mark a query as executed (deprecated - use execute instead)
   */
  async markAsExecuted(token: string, id: string): Promise<SavedQuery> {
    const response = await fetch(`${this.baseUrl}/${id}/mark-executed`, {
      method: 'PATCH',
      headers: this.getHeaders(token),
    });

    if (!response.ok) {
      throw new Error(`Failed to mark query as executed: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete a saved query
   */
  async delete(token: string, id: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(token),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete saved query: ${response.statusText}`);
    }
  }
}

export const savedQueriesService = new SavedQueriesService();
